import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iq/react-components';
import classnames from 'classnames';

import SimpleModal from '../../SimpleModal';
import { WizardTypeStep, StepIndicator } from './components';
import { requestSources, getSources, getSourcesLoaded } from '../../../bundles/sources';
import { updateSite as setCreationState } from '../../../bundles/sites';
import {
  requestModels,
  getModelIdsWithProcessingVersions,
  getModelsLoaded,
} from '../../../bundles/models';
import InstructionalModal from '../../InstructionalModal';
import {
  getCompletedSteps,
  getWizardHeaderTitle,
  getWizardSteps,
  getWizardTheme,
  hasAvailableWizardStep,
  wizardStepRequiresSources,
  wizardStepShowsLoading,
} from './wizardConfig.js';
import config from '../../../config';

const WIZARD_SELECT_STEP = {
  component: WizardTypeStep,
  name: 'select',
};

const InitComponent = ({ isOnPrem }) => {
  const [showInstruction, setShowInstruction] = useState(false);
  const fullScreenReference = useRef(null);

  return (
    <div className="wizard-info-init">
      <p>
        {`As a site manager, you can create essential content such as the component tree, 
        content panels${isOnPrem ? '' : ', and upload CAD models'}, etc. for your target audience.`}
      </p>
      <div className="cta-image-wrapper">
        <div className="cta-image" />
      </div>
      {/* <Button
        design="text"
        icon={<Icon icon="he-popup" />}
        onClick={() => setShowInstruction(true)}>
        Site manager training tutorial
      </Button> */}
      {showInstruction && (
        <InstructionalModal
          fullScreenRef={fullScreenReference}
          onClose={() => setShowInstruction(false)}
        />
      )}
    </div>
  );
};

const Wizard = ({ site, onClose }) => {
  const dispatch = useDispatch();
  const isOnPrem = Boolean(config?.onPremise);
  const sources = useSelector(getSources);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const modelsLoaded = useSelector(getModelsLoaded);
  const processingIds = useSelector(getModelIdsWithProcessingVersions);
  const wizardHasModelStep = hasAvailableWizardStep('model');

  const [step, setStep] = useState(0);
  const [wizardType, setWizardType] = useState(null);
  const wizardTheme = getWizardTheme(wizardType);

  const wizardSteps = useMemo(
    () => [WIZARD_SELECT_STEP, ...getWizardSteps(wizardType)],
    [wizardType]
  );
  const { component: StepComponent } = wizardSteps[step] || WIZARD_SELECT_STEP;

  const completedSteps = useMemo(() => getCompletedSteps(site, wizardType), [site, wizardType]);
  const completedStepNames = completedSteps.map(({ name }) => name);

  useEffect(() => {
    if (sourcesLoaded !== site.id)
      dispatch(requestSources({ siteId: site.id, withVariables: false })); // pre-fetch for source step
    if (wizardHasModelStep && !modelsLoaded) dispatch(requestModels(site.id)); // pre-fetch for model step, processing check
  }, [sourcesLoaded, modelsLoaded, site.id, dispatch, wizardHasModelStep]);

  const restart = () => {
    setWizardType(null);
    setStep(0);
  };

  const previousStep = () => {
    if (step === 1) {
      restart();
      return;
    }

    setStep((currentStep) => Math.max(currentStep - 1, 0));
  };

  const nextStep = () => {
    const currentStepName = wizardSteps[step].name;
    const isCurrentStepComplete = completedStepNames.includes(currentStepName);

    if (step !== 0 && !isCurrentStepComplete) {
      const unchangedState = (site.creationState || []).filter(({ name }) => name !== wizardType);

      dispatch(
        setCreationState(site.id, {
          creationState: [
            ...unchangedState,
            { name: wizardType, steps: [...completedSteps, { name: currentStepName }] },
          ],
        })
      );
    }

    if (step === wizardSteps.length - 1) {
      onClose();
      return;
    }

    setStep((currentStep) => currentStep + 1);
  };

  const onWizardType = (selectedWizType) => {
    setWizardType(selectedWizType);
    setStep(1);
  };

  const headerTitle = getWizardHeaderTitle(wizardType);
  const selectedWizardHeader = headerTitle ? (
    <div className="step-header">{headerTitle}</div>
  ) : null;
  const header = wizardType ? (
    selectedWizardHeader
  ) : (
    <div className="cta-header">
      Set up your <strong>IdentiQ</strong> now.
    </div>
  );
  const wizardInfoClassName = classnames('setup-wizard', 'wizard-info', {
    'wizard-info--advanced': wizardTheme === 'advanced',
    'wizard-info--basic': wizardTheme === 'basic',
  });
  const wizardHeaderClassName = classnames('wizard-info__header', {
    'wizard-info__header--advanced': wizardTheme === 'advanced',
    'wizard-info__header--basic': wizardTheme === 'basic',
  });
  const wizardComponentClassName = classnames('setup-wizard-component', {
    'setup-wizard-component--advanced': wizardTheme === 'advanced',
    'setup-wizard-component--basic': wizardTheme === 'basic',
  });
  const showHeaderBackButton = step !== 0;

  const checkDisabled = useCallback(
    (wizStep) => wizardStepRequiresSources(wizStep.name) && sources.length === 0,
    [sources.length]
  );

  const stepIndicators = useMemo(
    () =>
      wizardSteps.slice(1).map((wizStep, index) => {
        const key = `step-${index}`;
        return (
          <StepIndicator
            key={key}
            title={wizStep.title}
            icon={wizStep.icon}
            active={step === index + 1}
            checked={completedStepNames.includes(wizStep.name)}
            wizardTheme={wizardTheme}
            onSelect={() => setStep(index + 1)}
            disabled={checkDisabled(wizStep)}
            loading={wizardStepShowsLoading(wizStep.name) && processingIds.length > 0}
          />
        );
      }),
    [wizardSteps, step, completedStepNames, processingIds, checkDisabled, wizardTheme]
  );

  return (
    <SimpleModal
      className="setup-wizard-wrapper"
      size="l"
      onClose={onClose}
      overlayCanClose={false}
      noHeader="noHeader"
      noBodyPadding
    >
      <Icon
        size="xs"
        icon="he-close"
        onClick={onClose}
      />
      <div className={wizardInfoClassName}>
        <div className={wizardHeaderClassName}>
          {showHeaderBackButton ? (
            <button
              type="button"
              className="wizard-info__back-button"
              aria-label="Back"
              onClick={restart}
            >
              <Icon
                size="m"
                icon="he-backwards"
              />
              <span>Back</span>
            </button>
          ) : null}
          {header}
        </div>
        {step === 0 ? (
          <InitComponent isOnPrem={isOnPrem} />
        ) : (
          <ul className="step-indicator-container">{stepIndicators}</ul>
        )}
      </div>
      <div className={wizardComponentClassName}>
        <StepComponent
          site={site}
          step={step}
          wizardType={wizardType}
          onWizardType={onWizardType}
          onBack={previousStep}
          onNext={nextStep}
        />
      </div>
    </SimpleModal>
  );
};

export default Wizard;
